import { useEffect, useRef } from "react";
import { useCommandPaletteStore } from "@/components/grooming/use-command-registry";

/**
 * The backlog's keyboard shortcuts (epic PI-SPEC-SW-E280, feature
 * PI-SPEC-SW-1401): J or ↓ next item, K or ↑ previous item, A add to (or take
 * off) the session, B switch List / Stages, Esc close the pane, / focus the
 * find box. Ignored while the person types in a field, while the command
 * palette or a dialog is open, and with Ctrl, ⌘ or Alt held (⌘K belongs to
 * the palette).
 */

export interface BacklogShortcutHandlers {
  onNext: () => void;
  onPrevious: () => void;
  onToggleSession: () => void;
  onToggleView: () => void;
  onEscape: () => void;
  onFocusFind: () => void;
}

/** True when the key press belongs to a field the person is typing in. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag !== "INPUT") return false;
  const type = (target as HTMLInputElement).type;
  return !["button", "checkbox", "radio", "submit", "reset", "range", "color", "file"].includes(type);
}

/** True when a modal dialog is open over the page (Post all, the palette's sheet). */
function dialogOpen(): boolean {
  return document.querySelector('[role="dialog"], [role="alertdialog"]') !== null;
}

/** The action a key press asks for, or null. Pure; exported for tests. */
export function shortcutFor(key: string): keyof BacklogShortcutHandlers | null {
  switch (key) {
    case "j":
    case "J":
    case "ArrowDown":
      return "onNext";
    case "k":
    case "K":
    case "ArrowUp":
      return "onPrevious";
    case "a":
    case "A":
      return "onToggleSession";
    case "b":
    case "B":
      return "onToggleView";
    case "Escape":
      return "onEscape";
    case "/":
      return "onFocusFind";
    default:
      return null;
  }
}

export function useBacklogShortcuts(handlers: BacklogShortcutHandlers, enabled = true): void {
  const latest = useRef(handlers);
  useEffect(() => {
    latest.current = handlers;
  }, [handlers]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      if (useCommandPaletteStore.getState().open || dialogOpen()) return;
      const action = shortcutFor(event.key);
      if (!action) return;
      event.preventDefault();
      latest.current[action]();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [enabled]);
}
